import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { SlackUser } from '@assembly/slack'
import { useActions } from '../data/actions'
import { useConversationName, useConversations, useMembers, usePublicChannels, useReminders, useScheduledMessages, useUser, useUsers, displayName, fullName } from '../data/hooks'
import { useMe, useWorkspace } from '../data/workspace'
import { keys } from '../data/keys'
import { useUi, type ModalState } from '../state/ui'
import { formatBytes, formatDateTime, formatShortDate } from '../format'
import { EmojiPicker } from './EmojiPicker'
import { Icon } from './Icon'
import { ExternalLink, Files } from './MessageContent'
import { QuickSwitcher } from './QuickSwitcher'
import { AiOrganizeModal } from './AiOrganize'
import { useSidebar } from '../state/sidebar'
import { createSection, renameSection } from '../sidebar/layout'
import { Avatar, Emoji, IconButton, Modal, Spinner, anchorFrom, type Anchor } from './common'

const close = () => useUi.getState().openModal(null)

function useSubmit() {
  const [busy, setBusy] = useState(false)
  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true)
    try {
      await fn()
      close()
    } catch {
      /* the action already showed a toast */
    } finally {
      setBusy(false)
    }
  }
  return { busy, run }
}

function Footer({ busy, label, disabled, onSubmit, danger }: { busy: boolean; label: string; disabled?: boolean; onSubmit: () => void; danger?: boolean }) {
  return (
    <>
      <button type="button" className="button" onClick={close}>
        Cancel
      </button>
      <button type="button" className={`button ${danger ? 'danger' : 'primary'}`} disabled={disabled || busy} onClick={onSubmit}>
        {busy ? <Spinner size={14} /> : label}
      </button>
    </>
  )
}

// ------------------------------------------------------------------ channels

function CreateChannelModal() {
  const actions = useActions()
  const { busy, run } = useSubmit()
  const [name, setName] = useState('')
  const [purpose, setPurpose] = useState('')
  const [isPrivate, setPrivate] = useState(false)
  const normalized = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9_\-À-ɏ]/g, '')
  const submit = () => normalized && run(() => actions.createChannel(normalized, isPrivate, purpose.trim() || undefined))
  return (
    <Modal title="Create a channel" onClose={close} footer={<Footer busy={busy} label="Create" disabled={!normalized} onSubmit={submit} />}>
      <label className="field">
        <span>Name</span>
        <div className="input-with-icon">
          <Icon name={isPrivate ? 'lock' : 'hash'} size={16} />
          <input autoFocus value={name} maxLength={80} placeholder="e.g. plan-budget" onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submit()} />
        </div>
        {name && normalized !== name && <small className="muted">Will be created as #{normalized}</small>}
      </label>
      <label className="field">
        <span>
          Description <span className="muted">(optional)</span>
        </span>
        <input value={purpose} placeholder="What’s this channel about?" onChange={(e) => setPurpose(e.target.value)} />
      </label>
      <label className="toggle-row">
        <input type="checkbox" checked={isPrivate} onChange={(e) => setPrivate(e.target.checked)} />
        <span>
          <strong>Make private</strong>
          <small className="muted">Only invited people can view and join a private channel.</small>
        </span>
      </label>
    </Modal>
  )
}

function BrowseChannelsModal() {
  const [includeArchived, setIncludeArchived] = useState(false)
  const { data: channels, isLoading } = usePublicChannels(includeArchived)
  const { data: mine } = useConversations()
  const actions = useActions()
  const openChannel = useUi((s) => s.openChannel)
  const openModal = useUi((s) => s.openModal)
  const [q, setQ] = useState('')
  const [joining, setJoining] = useState<string | null>(null)
  const memberOf = useMemo(() => new Set((mine ?? []).map((c) => c.id)), [mine])
  const list = (channels ?? [])
    .filter((c) => !q || c.name?.includes(q.toLowerCase()) || c.purpose?.value.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (b.num_members ?? 0) - (a.num_members ?? 0))
  return (
    <Modal title="Browse channels" onClose={close} wide>
      <div className="row">
        <input autoFocus className="search-input grow" placeholder="Search for channels" value={q} onChange={(e) => setQ(e.target.value)} />
        <button type="button" className="button" onClick={() => openModal({ type: 'createChannel' })}>
          <Icon name="plus" size={15} /> Create
        </button>
      </div>
      <label className="toggle-row compact">
        <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />
        <span>Include archived channels</span>
      </label>
      <div className="browse-list">
        {isLoading && <Spinner />}
        {list.map((c) => {
          const member = memberOf.has(c.id)
          return (
            <div key={c.id} className="browse-row">
              <button
                type="button"
                className="browse-main"
                onClick={() => {
                  close()
                  openChannel(c.id)
                }}
              >
                <strong>
                  <Icon name={c.is_archived ? 'archive' : 'hash'} size={14} /> {c.name}
                </strong>
                <span className="muted">
                  {member && <span className="joined">✓ Joined · </span>}
                  {c.num_members ?? 0} members{c.purpose?.value ? ` · ${c.purpose.value}` : ''}
                </span>
              </button>
              {!c.is_archived &&
                (member ? (
                  <button type="button" className="button" onClick={() => actions.leaveChannel(c.id)}>
                    Leave
                  </button>
                ) : (
                  <button
                    type="button"
                    className="button primary"
                    disabled={joining === c.id}
                    onClick={async () => {
                      setJoining(c.id)
                      try {
                        await actions.joinChannel(c.id)
                      } finally {
                        setJoining(null)
                      }
                    }}
                  >
                    {joining === c.id ? <Spinner size={14} /> : 'Join'}
                  </button>
                ))}
            </div>
          )
        })}
        {!isLoading && !list.length && <p className="muted pad">No channels match “{q}”.</p>}
      </div>
    </Modal>
  )
}

// ------------------------------------------------------------------ people

function UserPicker({ selected, onChange, exclude = [] }: { selected: string[]; onChange: (ids: string[]) => void; exclude?: string[] }) {
  const { data: users } = useUsers()
  const [q, setQ] = useState('')
  const excluded = new Set([...exclude, ...selected])
  const query = q.toLowerCase()
  const results = [...(users?.values() ?? [])]
    .filter((u) => !u.deleted && !u.is_bot && u.id !== 'USLACKBOT' && !excluded.has(u.id))
    .filter((u) => !query || displayName(u).toLowerCase().includes(query) || fullName(u).toLowerCase().includes(query) || u.profile.email?.toLowerCase().includes(query))
    .slice(0, 30)
  return (
    <div className="user-picker">
      <div className="chips-input">
        {selected.map((id) => (
          <PickedChip key={id} id={id} onRemove={() => onChange(selected.filter((x) => x !== id))} />
        ))}
        <input
          autoFocus
          value={q}
          placeholder={selected.length ? '' : 'Search by name or email'}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !q && selected.length) onChange(selected.slice(0, -1))
            if (e.key === 'Enter' && results[0]) {
              onChange([...selected, results[0].id])
              setQ('')
            }
          }}
        />
      </div>
      <div className="picker-results">
        {results.map((u: SlackUser) => (
          <button
            key={u.id}
            type="button"
            className="picker-row"
            onClick={() => {
              onChange([...selected, u.id])
              setQ('')
            }}
          >
            <Avatar user={u} size={28} />
            <strong>{displayName(u)}</strong>
            {fullName(u) !== displayName(u) && <span className="muted">{fullName(u)}</span>}
          </button>
        ))}
      </div>
    </div>
  )
}

function PickedChip({ id, onRemove }: { id: string; onRemove: () => void }) {
  const user = useUser(id)
  return (
    <span className="chip">
      <Avatar user={user} size={18} />
      {displayName(user, id)}
      <button type="button" aria-label="Remove" onClick={onRemove}>
        <Icon name="x" size={12} />
      </button>
    </span>
  )
}

function NewMessageModal() {
  const actions = useActions()
  const me = useMe()
  const { busy, run } = useSubmit()
  const [selected, setSelected] = useState<string[]>([])
  return (
    <Modal
      title="New message"
      onClose={close}
      footer={<Footer busy={busy} label={selected.length > 1 ? 'Start group message' : 'Open'} disabled={!selected.length} onSubmit={() => run(() => actions.openDirectMessage(selected))} />}
    >
      <p className="muted">Start a direct message with one person, or a group conversation with up to 8.</p>
      <UserPicker selected={selected} onChange={(ids) => setSelected(ids.slice(0, 8))} exclude={[me]} />
      <button type="button" className="link-button" onClick={() => run(() => actions.openDirectMessage([me]))}>
        Message yourself
      </button>
    </Modal>
  )
}

function InviteModal({ channel }: { channel: string }) {
  const actions = useActions()
  const { data: members } = useMembers(channel)
  const { data: conv } = useConversations()
  const name = useConversationName()
  const { busy, run } = useSubmit()
  const [selected, setSelected] = useState<string[]>([])
  const c = conv?.find((x) => x.id === channel)
  return (
    <Modal title={`Add people to ${c ? `#${name(c)}` : 'channel'}`} onClose={close} footer={<Footer busy={busy} label="Add" disabled={!selected.length} onSubmit={() => run(() => actions.invite(channel, selected))} />}>
      <UserPicker selected={selected} onChange={setSelected} exclude={members ?? []} />
    </Modal>
  )
}

// ------------------------------------------------------------------ profile & status

function EditProfileModal() {
  const me = useMe()
  const user = useUser(me)
  const actions = useActions()
  const { busy, run } = useSubmit()
  const [form, setForm] = useState({
    real_name: user?.profile.real_name ?? '',
    display_name: user?.profile.display_name ?? '',
    title: user?.profile.title ?? '',
    phone: user?.profile.phone ?? '',
  })
  const field = (key: keyof typeof form, label: string, hint?: string) => (
    <label className="field">
      <span>{label}</span>
      <input value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
      {hint && <small className="muted">{hint}</small>}
    </label>
  )
  return (
    <Modal title="Edit your profile" onClose={close} footer={<Footer busy={busy} label="Save changes" onSubmit={() => run(() => actions.setProfile(form))} />}>
      <div className="profile-edit">
        <div className="grow">
          {field('real_name', 'Full name')}
          {field('display_name', 'Display name', 'This could be your first name, or a nickname.')}
          {field('title', 'Title', 'Let people know what you do.')}
          {field('phone', 'Phone')}
        </div>
        <Avatar user={user} size={120} />
      </div>
      <p className="muted small">Some fields may be managed by your workspace admin. Change your photo in Slack.</p>
    </Modal>
  )
}

const STATUS_PRESETS = [
  { emoji: ':spiral_calendar_pad:', text: 'In a meeting', minutes: 60 },
  { emoji: ':bus:', text: 'Commuting', minutes: 30 },
  { emoji: ':face_with_thermometer:', text: 'Out sick', minutes: 0 },
  { emoji: ':palm_tree:', text: 'Vacationing', minutes: 0 },
  { emoji: ':house_with_garden:', text: 'Working remotely', minutes: 0 },
  { emoji: ':headphones:', text: 'Focusing', minutes: 120 },
]

function SetStatusModal() {
  const me = useMe()
  const user = useUser(me)
  const actions = useActions()
  const { busy, run } = useSubmit()
  const [emoji, setEmoji] = useState(user?.profile.status_emoji ?? '')
  const [text, setText] = useState(user?.profile.status_text ?? '')
  const [clearAfter, setClearAfter] = useState(0)
  const [picker, setPicker] = useState<Anchor | null>(null)
  const expiration = clearAfter ? Math.floor(Date.now() / 1000) + clearAfter * 60 : 0
  return (
    <Modal
      title="Set a status"
      onClose={close}
      footer={
        <>
          {(user?.profile.status_text || user?.profile.status_emoji) && (
            <button type="button" className="button" onClick={() => run(() => actions.setStatus('', ''))}>
              Clear status
            </button>
          )}
          <span className="grow" />
          <Footer busy={busy} label="Save" onSubmit={() => run(() => actions.setStatus(text, emoji || (text ? ':speech_balloon:' : ''), expiration))} />
        </>
      }
    >
      <div className="status-input">
        <button type="button" className="icon-button" onClick={(e) => setPicker(anchorFrom(e))} aria-label="Pick emoji">
          {emoji ? <Emoji name={emoji.replace(/:/g, '')} size={20} /> : <Icon name="smile" />}
        </button>
        <input autoFocus value={text} maxLength={100} placeholder="What’s your status?" onChange={(e) => setText(e.target.value)} />
      </div>
      <div className="status-presets">
        {STATUS_PRESETS.map((p) => (
          <button
            key={p.text}
            type="button"
            onClick={() => {
              setEmoji(p.emoji)
              setText(p.text)
              setClearAfter(p.minutes)
            }}
          >
            <Emoji name={p.emoji.replace(/:/g, '')} /> {p.text} <span className="muted">{p.minutes ? `· ${p.minutes >= 60 ? `${p.minutes / 60} hour${p.minutes > 60 ? 's' : ''}` : `${p.minutes} minutes`}` : ''}</span>
          </button>
        ))}
      </div>
      <label className="field">
        <span>Clear after</span>
        <select value={clearAfter} onChange={(e) => setClearAfter(Number(e.target.value))}>
          <option value={0}>Don’t clear</option>
          <option value={30}>30 minutes</option>
          <option value={60}>1 hour</option>
          <option value={240}>4 hours</option>
          <option value={60 * 24}>Today</option>
          <option value={60 * 24 * 7}>This week</option>
        </select>
      </label>
      {picker && <EmojiPicker anchor={picker} placement="below" onClose={() => setPicker(null)} onSelect={(n) => setEmoji(`:${n}:`)} />}
    </Modal>
  )
}

// ------------------------------------------------------------------ reminders / scheduled / files

function RemindersModal() {
  const { data: reminders, isLoading } = useReminders()
  const actions = useActions()
  const [text, setText] = useState('')
  const [when, setWhen] = useState('in 1 hour')
  const me = useMe()
  const pending = (reminders ?? []).filter((r) => !r.complete_ts && r.user === me).sort((a, b) => (a.time ?? 0) - (b.time ?? 0))
  const done = (reminders ?? []).filter((r) => r.complete_ts && r.user === me).slice(0, 10)
  return (
    <Modal title="Reminders" onClose={close} wide>
      <form
        className="reminder-form"
        onSubmit={async (e) => {
          e.preventDefault()
          if (!text.trim() || !when.trim()) return
          await actions.addReminder(text.trim(), when.trim()).then(() => setText(''), () => undefined)
        }}
      >
        <input autoFocus className="grow" placeholder="Remind me to…" value={text} onChange={(e) => setText(e.target.value)} />
        <input className="when" placeholder="in 1 hour, tomorrow at 9am…" value={when} onChange={(e) => setWhen(e.target.value)} />
        <button type="submit" className="button primary" disabled={!text.trim()}>
          Add
        </button>
      </form>
      {isLoading && <Spinner />}
      {!isLoading && !pending.length && <p className="muted pad">No upcoming reminders.</p>}
      {pending.map((r) => (
        <div key={r.id} className="list-row">
          <button type="button" className="check" aria-label="Complete" onClick={() => actions.completeReminder(r.id)} />
          <div className="grow">
            <div>{r.text}</div>
            <small className="muted">{r.recurring ? 'Recurring' : r.time ? formatDateTime(r.time) : ''}</small>
          </div>
          <IconButton icon="trash" label="Delete reminder" size={15} onClick={() => actions.deleteReminder(r.id)} />
        </div>
      ))}
      {done.length > 0 && <h4 className="list-heading">Completed</h4>}
      {done.map((r) => (
        <div key={r.id} className="list-row done">
          <span className="check checked">
            <Icon name="check" size={12} />
          </span>
          <div className="grow">{r.text}</div>
          <IconButton icon="trash" label="Delete reminder" size={15} onClick={() => actions.deleteReminder(r.id)} />
        </div>
      ))}
    </Modal>
  )
}

function ScheduledModal() {
  const { data: scheduled, isLoading } = useScheduledMessages()
  const { data: convs } = useConversations()
  const name = useConversationName()
  const actions = useActions()
  return (
    <Modal title="Scheduled messages" onClose={close} wide>
      {isLoading && <Spinner />}
      {!isLoading && !scheduled?.length && <p className="muted pad">Nothing scheduled. Use the arrow next to Send to schedule a message.</p>}
      {(scheduled ?? [])
        .sort((a, b) => a.post_at - b.post_at)
        .map((s) => {
          const c = convs?.find((x) => x.id === s.channel_id)
          return (
            <div key={s.id} className="list-row">
              <Icon name="clock" size={16} />
              <div className="grow">
                <small className="muted">
                  {formatDateTime(s.post_at)} · {c ? (c.is_im ? name(c) : `#${name(c)}`) : s.channel_id}
                </small>
                <div className="clamp">{s.text}</div>
              </div>
              <IconButton icon="trash" label="Delete scheduled message" size={15} onClick={() => actions.deleteScheduled(s.channel_id, s.id)} />
            </div>
          )
        })}
    </Modal>
  )
}

function FilesModal() {
  const { account, client } = useWorkspace()
  const me = useMe()
  const actions = useActions()
  const [mineOnly, setMineOnly] = useState(false)
  const [types, setTypes] = useState('all')
  const { data, isLoading } = useQuery({
    queryKey: keys.files(account.id, `${mineOnly}:${types}`),
    queryFn: () => client.files.list({ user: mineOnly ? me : undefined, types }),
  })
  return (
    <Modal title="Files" onClose={close} wide>
      <div className="row">
        <select value={types} onChange={(e) => setTypes(e.target.value)}>
          <option value="all">All types</option>
          <option value="images">Images</option>
          <option value="pdfs">PDFs</option>
          <option value="snippets">Snippets</option>
          <option value="spaces">Posts</option>
          <option value="zips">Zips</option>
        </select>
        <label className="toggle-row compact">
          <input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} />
          <span>Only my files</span>
        </label>
      </div>
      {isLoading && <Spinner />}
      {!isLoading && !data?.files.length && <p className="muted pad">No files.</p>}
      <div className="files-list">
        {(data?.files ?? []).map((f) => (
          <div key={f.id} className="files-list-row">
            <Files files={[f]} />
            <div className="files-list-meta muted">
              <FileOwner id={f.user} /> · {f.created ? formatShortDate(f.created) : ''} · {formatBytes(f.size)}
              {f.user === me && (
                <button
                  type="button"
                  className="link-button danger"
                  onClick={() =>
                    useUi.getState().openModal({ type: 'confirm', title: 'Delete file?', body: `“${f.title ?? f.name}” will be removed from every conversation it was shared in.`, confirm: 'Delete', danger: true, onConfirm: () => actions.deleteFile(f.id) })
                  }
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </Modal>
  )
}

function FileOwner({ id }: { id?: string }) {
  const user = useUser(id)
  return <>{displayName(user, '')}</>
}

function AddBookmarkModal({ channel }: { channel: string }) {
  const actions = useActions()
  const { busy, run } = useSubmit()
  const [link, setLink] = useState('')
  const [title, setTitle] = useState('')
  const valid = /^https?:\/\/\S+\.\S+/.test(link)
  return (
    <Modal title="Add a bookmark" onClose={close} footer={<Footer busy={busy} label="Add" disabled={!valid} onSubmit={() => run(() => actions.addBookmark(channel, title.trim() || link, link.trim()))} />}>
      <label className="field">
        <span>Link</span>
        <input autoFocus value={link} placeholder="https://" onChange={(e) => setLink(e.target.value)} />
      </label>
      <label className="field">
        <span>Name</span>
        <input value={title} placeholder="Optional" onChange={(e) => setTitle(e.target.value)} />
      </label>
      {link && !valid && <p className="field-error">Enter a full URL starting with https://</p>}
      {valid && (
        <p className="muted small">
          Preview: <ExternalLink href={link}>{title || link}</ExternalLink>
        </p>
      )}
    </Modal>
  )
}

function ConfirmModal({ modal }: { modal: Extract<ModalState, { type: 'confirm' }> }) {
  const { busy, run } = useSubmit()
  return (
    <Modal title={modal.title} onClose={close} footer={<Footer busy={busy} label={modal.confirm} danger={modal.danger} onSubmit={() => run(async () => modal.onConfirm())} />}>
      <p>{modal.body}</p>
    </Modal>
  )
}

// ------------------------------------------------------------------ sidebar sections

function SectionModal({ id, conv }: { id?: string; conv?: string }) {
  const existing = useSidebar((s) => (id ? s.layout.sections.find((x) => x.id === id) : undefined))
  const edit = useSidebar((s) => s.edit)
  const [name, setName] = useState(existing?.name ?? '')
  const [emoji, setEmoji] = useState(existing?.emoji ?? '')
  const [picker, setPicker] = useState<Anchor | null>(null)
  const [error, setError] = useState<string | null>(null)
  const submit = () => {
    if (!name.trim()) return
    try {
      edit((l) => (existing ? renameSection(l, existing.id, name, emoji || null) : createSection(l, name, { emoji: emoji || undefined, items: conv ? [conv] : [] }).layout))
      close()
    } catch (e) {
      setError((e as Error).message)
    }
  }
  return (
    <Modal
      title={existing ? 'Edit section' : 'Create a section'}
      onClose={close}
      footer={<Footer busy={false} label={existing ? 'Save' : 'Create'} disabled={!name.trim()} onSubmit={submit} />}
    >
      <label className="field">
        <span>Name</span>
        <div className="status-input">
          <button type="button" className="icon-button" onClick={(e) => setPicker(anchorFrom(e))} aria-label="Pick an emoji for the section">
            {emoji ? <Emoji name={emoji} size={20} /> : <Icon name="smile" />}
          </button>
          <input autoFocus value={name} maxLength={80} placeholder="e.g. Projects" aria-label="Section name" onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && submit()} />
        </div>
        {error && <small className="field-error">{error}</small>}
        {!existing && <small className="muted">Drag conversations into it, or use “Move to section” on any conversation.</small>}
      </label>
      {picker && <EmojiPicker anchor={picker} placement="below" onClose={() => setPicker(null)} onSelect={(n) => (setEmoji(n), setPicker(null))} />}
    </Modal>
  )
}

const SHORTCUTS: [string, string][] = [
  ['⌘K / ⌘T', 'Jump to a conversation or person'],
  ['⌘G', 'Search messages'],
  ['⌘N', 'New message'],
  ['⌘⇧L', 'Browse channels'],
  ['⌥↑ / ⌥↓', 'Previous / next conversation'],
  ['⌘1…9', 'Switch workspace'],
  ['↑', 'Edit your last message'],
  ['⌘B / ⌘I', 'Bold / italic'],
  ['⌘⇧X', 'Strikethrough'],
  ['⌘⇧C', 'Inline code'],
  ['⇧↵', 'New line'],
  ['Esc', 'Close panel or cancel edit'],
  ['⌘/', 'Show this list'],
]

function ShortcutsModal() {
  return (
    <Modal title="Keyboard shortcuts" onClose={close}>
      <dl className="shortcuts">
        {SHORTCUTS.map(([k, v]) => (
          <div key={k}>
            <dt>
              <kbd>{k}</kbd>
            </dt>
            <dd>{v}</dd>
          </div>
        ))}
      </dl>
    </Modal>
  )
}

export function ModalHost() {
  const modal = useUi((s) => s.modal)
  if (!modal) return null
  switch (modal.type) {
    case 'createChannel':
      return <CreateChannelModal />
    case 'browseChannels':
      return <BrowseChannelsModal />
    case 'newMessage':
      return <NewMessageModal />
    case 'invite':
      return <InviteModal channel={modal.channel} />
    case 'editProfile':
      return <EditProfileModal />
    case 'setStatus':
      return <SetStatusModal />
    case 'reminders':
      return <RemindersModal />
    case 'scheduled':
      return <ScheduledModal />
    case 'files':
      return <FilesModal />
    case 'addBookmark':
      return <AddBookmarkModal channel={modal.channel} />
    case 'confirm':
      return <ConfirmModal modal={modal} />
    case 'shortcuts':
      return <ShortcutsModal />
    case 'switcher':
      return <QuickSwitcher initialQuery={modal.query} />
    case 'section':
      return <SectionModal id={modal.id} conv={modal.conv} />
    case 'aiOrganize':
      return <AiOrganizeModal />
  }
}
